/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { PlanId } from "components/common/boundary/PlanId";
import { OverrideCell } from "components/common/plan/OverrideCell";
import { createPlanMixUpdates } from "components/common/plan/EditableTableActions";
import {
  filterPlanResultsToPeriod,
  getMaterialMass,
  renumberBasketLayers,
} from "components/common/plan/utils";
import { SteelGrade } from "contexts/search/context";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { Period } from "hooks/periodIndex";
import { useSearchId } from "hooks/search";
import { useTenant } from "hooks/settings";
import { useTenantData } from "contexts/tenant";
import { usePeriodNameFormatter } from "hooks/usePeriodNameFormatter";
import React, { useCallback, useState } from "react";
import {
  generatedApi,
  MaterialRead,
  MixBasket,
  MixBasketLayer,
  PeriodSummary,
  ProductionMixResultRead,
  SummarisedProductionMixResultRead,
  useGetPlanQuery,
  useGetPlanResultsQuery,
  useSummarisePlanResultsMutation,
  useUpdatePlanResultsMutation,
} from "src/store/api/generatedApi";
import {
  deployPlanFormSlice,
  selectDeployPlanFormChecked,
} from "src/store/slices/deployPlanFormSlice";
import { makePlanPeriodId } from "src/store/slices/planMixesTableEditSlice";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { getMixIdsRequiringMixCodeOverride } from "./utils";

const CostDisplay = ({
  originalCost,
  currentCost,
}: {
  originalCost: number | null;
  currentCost: number | null;
}) => {
  const theme = useTheme();
  const unit = useUnitsFormatter(false);

  if (originalCost === null && currentCost === null) return null;

  const displayOriginal = originalCost ?? currentCost;
  const hasChanged =
    currentCost !== null &&
    originalCost !== null &&
    currentCost.toFixed(2) !== originalCost.toFixed(2);

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Typography variant="body2" color="text.secondary">
        {displayOriginal?.toFixed(2)}
        {unit("specific_cost")}
      </Typography>
      {hasChanged ? (
        <Typography
          variant="body2"
          sx={{
            px: 0.5,
            borderRadius: 0.5,
            bgcolor:
              currentCost < originalCost
                ? theme.palette.success.main
                : theme.palette.error.main,
            color: "white",
          }}
        >
          {currentCost.toFixed(2)}
          {unit("specific_cost")}
        </Typography>
      ) : null}
    </Stack>
  );
};

const CopperDisplay = ({ copper }: { copper: number | null }) => {
  if (copper === null) return null;

  return (
    <Typography variant="body2" color="text.secondary">
      {"Cu"} {copper.toFixed(3)}%
    </Typography>
  );
};

interface Props {
  preview: {
    planId: PlanId;
    period: Period;
    title: string;
    isBackup: boolean;
  } | null;
  periods: PeriodSummary[];
  planId: PlanId;
  setPlanId: (planId: PlanId) => void;
  steelGrades: SteelGrade[];
  materials: MaterialRead[];
}

interface MixCardData {
  mixId: number;
  chefGroupId: number;
  groupName: string;
  numHeats: number;
  originalCostPerTonne: number | null;
  currentCostPerTonne: number | null;
  copperPercent: number | null;
  steelGrades: string[];
  checkedMix: { planId: PlanId; period: Period } | null;
  requiresMixCodeOverride: boolean;
}

type EditedMixesState = Record<number, Record<string, Record<string, number>>>;

// Each cell of the mobile edit table represents the *total* mass for a material
// in a basket; a basket may carry multiple physical layers of that same
// material. Applying an edit replaces every layer for the edited material with
// a single consolidated layer at the new value, preserving the unit of the
// first matching layer (or "mass" by default for newly-added materials).
// renumberBasketLayers handles the final 1-based numbering that the backend
// schema requires.
const applyEditsToBaskets = (
  baskets: MixBasket[],
  editedBaskets: Record<string, Record<string, number>> | undefined
): MixBasket[] => {
  if (!editedBaskets) return baskets;
  const updated = baskets.map((basket) => {
    const basketEdits = editedBaskets[String(basket.basket_number)];
    if (!basketEdits) return basket;
    const editedNames = new Set(Object.keys(basketEdits));
    const untouchedLayers = basket.layers.filter(
      (layer) => !editedNames.has(layer.material_name)
    );
    const consolidatedLayers: MixBasketLayer[] = Object.entries(
      basketEdits
    ).map(([materialName, value]) => {
      const existingUnit =
        basket.layers.find((layer) => layer.material_name === materialName)
          ?.quantity.unit ?? "mass";
      return {
        material_name: materialName,
        quantity: { value, unit: existingUnit },
        layer_number: 0,
      };
    });
    return { ...basket, layers: [...untouchedLayers, ...consolidatedLayers] };
  });
  return renumberBasketLayers(updated);
};

export const MobileDeployForm = ({
  preview,
  periods,
  planId,
  setPlanId,
  steelGrades,
  materials,
}: Props) => {
  const periodNameFormatter = usePeriodNameFormatter();
  const dispatch = useAppDispatch();
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const [selectedMix, setSelectedMix] = useState<MixCardData | null>(null);

  const [period] = periods;

  React.useLayoutEffect(() => {
    if (!preview && period) {
      dispatch(
        deployPlanFormSlice.actions.addPeriod({
          planId,
          period: period.period as Period,
        })
      );
      dispatch(
        deployPlanFormSlice.actions.setPreview([
          planId,
          {
            planId,
            period: period.period as Period,
            title: periodNameFormatter(period.period as Period, {
              startTimestamp: period.start_timestamp,
              endTimestamp: period.end_timestamp,
              name: period.name,
            }),
            isBackup: false,
          },
        ])
      );
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- period is destructured from periods; the effect should only re-run when periods/preview change, not on every render
  }, [periods, preview, dispatch, planId, periodNameFormatter]);

  const focussedPeriod = preview?.period ?? (period?.period as Period);

  const { default_mix_breakdown, small_mass_per_mass, manual_heat_overrides } =
    useTenantData();
  const massScaleFactor =
    default_mix_breakdown === "small_mass" ? small_mass_per_mass : 1;

  const { currentData: plan, isLoading: isPlanLoading } = useGetPlanQuery({
    tenantName,
    planId,
  });

  const { currentData: planResults, isLoading: isResultsLoading } =
    useGetPlanResultsQuery({
      tenantName,
      planId,
      period: focussedPeriod,
    });
  // Shadow query for migration diff coverage (Meticulous). Result unused.
  useGetPlanResultsQuery({
    tenantName,
    planId,
    period: focussedPeriod,
  });

  const periodResults = React.useMemo(
    () => planResults?.periods[focussedPeriod.toString()]?.mixes ?? [],
    [planResults, focussedPeriod]
  );

  const [editedMixes, setEditedMixes] = useState<EditedMixesState>({});

  const [originalSummary, setOriginalSummary] = useState<
    Record<
      number,
      { costPerTonne: number | null; copperPercent: number | null }
    >
  >({});

  React.useEffect(() => {
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- resets form state when plan/period changes
    setOriginalSummary({});
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- resets form state when plan/period changes
    setEditedMixes({});
  }, [planId, focussedPeriod]);

  const [doSummarisePlanResultsMutation, summariseResult] =
    useSummarisePlanResultsMutation({
      fixedCacheKey: makePlanPeriodId(planId, focussedPeriod),
    });

  React.useEffect(() => {
    if (!planResults || periodResults.length === 0) return;

    const unsummarisedMixes = periodResults.map((result) => ({
      original_mix_id: result.mix.id,
      name: result.name,
      description: result.description,
      production: result.production,
      mix: {
        baskets: applyEditsToBaskets(
          result.mix.baskets,
          editedMixes[result.mix.id]
        ),
        furnace: result.mix.furnace,
        thresholds: result.mix.thresholds,
      },
    }));

    const mutation = doSummarisePlanResultsMutation({
      tenantName,
      unsummarisedPlanResults: {
        plan_id: planId,
        period: focussedPeriod,
        mixes: unsummarisedMixes,
      },
    });

    return () => {
      mutation.abort();
    };
  }, [
    doSummarisePlanResultsMutation,
    planResults,
    periodResults,
    editedMixes,
    tenantName,
    planId,
    focussedPeriod,
  ]);

  const summarisedMixes = summariseResult.data?.mixes;

  React.useEffect(() => {
    if (
      !summarisedMixes ||
      summarisedMixes.length === 0 ||
      Object.keys(originalSummary).length > 0
    )
      return;

    const initial: Record<
      number,
      { costPerTonne: number | null; copperPercent: number | null }
    > = {};
    summarisedMixes.forEach((result) => {
      const copperChemistry = result.mix.summary.chemistry.find(
        (c) => c.constraint_name === "Cu"
      );
      initial[result.mix.id] = {
        costPerTonne: result.mix.summary.cost.display_cost_per_tonne,
        copperPercent: copperChemistry?.weight_percent_loc ?? null,
      };
    });
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- captures initial values after summarise completes
    setOriginalSummary(initial);
  }, [summarisedMixes, originalSummary]);

  const checkedMixes = useAppSelector((state) =>
    selectDeployPlanFormChecked(state, planId)
  );
  const mixIdsRequiringMixCodeOverride = React.useMemo(
    () =>
      new Set(
        getMixIdsRequiringMixCodeOverride(
          plan?.mix_metadata ?? [],
          focussedPeriod,
          manual_heat_overrides
        )
      ),
    [focussedPeriod, manual_heat_overrides, plan]
  );

  const mixCards: MixCardData[] = React.useMemo(() => {
    if (!plan?.summary.mix_summary) return [];

    return plan.summary.mix_summary
      .filter((mix) => mix.period === focussedPeriod)
      .map((mix) => {
        const groupSteelGrades = steelGrades.slice(0, 3).map((sg) => sg.name);

        const summarisedMix = summarisedMixes?.find(
          (sm) => sm.mix.id === mix.mix_id
        );
        const copperChemistry = summarisedMix?.mix.summary.chemistry.find(
          (c) => c.constraint_name === "Cu"
        );

        const original = originalSummary[mix.mix_id];

        return {
          mixId: mix.mix_id,
          chefGroupId: mix.chef_group_id,
          groupName: mix.chef_group_name ?? `Mix ${mix.mix_id}`,
          numHeats: mix.num_heats ?? 0,
          originalCostPerTonne:
            original?.costPerTonne ?? mix.display_cost_per_tonne ?? null,
          currentCostPerTonne:
            summarisedMix?.mix.summary.cost.display_cost_per_tonne ?? null,
          copperPercent: copperChemistry?.weight_percent_loc ?? null,
          steelGrades: groupSteelGrades,
          checkedMix: checkedMixes?.[mix.mix_id] ?? null,
          requiresMixCodeOverride: mixIdsRequiringMixCodeOverride.has(
            mix.mix_id
          ),
        };
      });
  }, [
    plan,
    focussedPeriod,
    steelGrades,
    checkedMixes,
    summarisedMixes,
    originalSummary,
    mixIdsRequiringMixCodeOverride,
  ]);

  const handleCardClick = (card: MixCardData) => {
    setSelectedMix(card);
  };

  const handleMassChange = useCallback(
    (
      mixId: number,
      basketNumber: string,
      materialName: string,
      mass: number
    ) => {
      setEditedMixes((prev) => ({
        ...prev,
        [mixId]: {
          ...(prev[mixId] ?? {}),
          [basketNumber]: {
            ...(prev[mixId]?.[basketNumber] ?? {}),
            [materialName]: mass,
          },
        },
      }));
    },
    []
  );

  const searchId = useSearchId();
  const [doUpdatePlanResultsMutation] = useUpdatePlanResultsMutation();
  const [getResult] = generatedApi.useLazyGetResultQuery();
  const [isSaving, setIsSaving] = useState(false);

  const hasEdits = Object.keys(editedMixes).length > 0;

  const handleDiscardChanges = useCallback(() => {
    setEditedMixes({});
    setOriginalSummary({});
  }, []);

  const handleSaveChanges = useCallback(async () => {
    if (!planResults || !hasEdits) return;

    setIsSaving(true);
    try {
      const newResults: ProductionMixResultRead[] = periodResults.map(
        (result) => {
          const mixEdits = editedMixes[result.mix.id];
          if (!mixEdits) return result;
          return {
            ...result,
            mix: {
              ...result.mix,
              baskets: applyEditsToBaskets(result.mix.baskets, mixEdits),
            },
          };
        }
      );

      const mixUpdates = createPlanMixUpdates(periodResults, newResults);

      if (mixUpdates.length > 0) {
        const { data: updatedPlan } = await doUpdatePlanResultsMutation({
          tenantName,
          planId,
          planMixUpdates: {
            period_updates: [
              {
                period: focussedPeriod,
                updates: mixUpdates,
                replacements: {},
              },
            ],
          },
        });

        if (updatedPlan) {
          const newPlanId = updatedPlan.plan_id as PlanId;

          void dispatch(
            generatedApi.util.upsertQueryData(
              "getPlanResults",
              { planId: newPlanId, tenantName, period: focussedPeriod },
              filterPlanResultsToPeriod(updatedPlan, focussedPeriod)
            )
          );

          if (searchId !== null) {
            await getResult({ searchId, tenantName });
          }

          setPlanId(newPlanId);
          setEditedMixes({});
          setOriginalSummary({});
        }
      }
    } finally {
      setIsSaving(false);
    }
  }, [
    planResults,
    periodResults,
    editedMixes,
    hasEdits,
    doUpdatePlanResultsMutation,
    tenantName,
    planId,
    focussedPeriod,
    dispatch,
    searchId,
    getResult,
    setPlanId,
  ]);

  const isLoading = isPlanLoading || isResultsLoading;

  if (isLoading || !preview) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <>
      <Stack spacing={2} sx={{ py: 2 }}>
        {mixCards.map((card) => (
          <Card
            key={card.mixId}
            variant="outlined"
            sx={{
              cursor: "pointer",
              "&:active": { bgcolor: "action.hover" },
            }}
            onClick={() => {
              handleCardClick(card);
            }}
          >
            <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {card.groupName}
                </Typography>
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ mt: 0.5 }}
                  flexWrap="wrap"
                >
                  <CostDisplay
                    originalCost={card.originalCostPerTonne}
                    currentCost={card.currentCostPerTonne}
                  />
                  <CopperDisplay copper={card.copperPercent} />
                  <Typography variant="body2" color="text.secondary">
                    {card.numHeats} {t("heats")}
                  </Typography>
                </Stack>
                {card.steelGrades.length > 0 && (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{ mt: 1 }}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    {card.steelGrades.map((grade) => (
                      <Chip
                        key={grade}
                        label={grade}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                )}
                {card.requiresMixCodeOverride && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mt: 1 }}
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                  >
                    <Typography variant="body2">{t("nameOverride")}</Typography>
                    <OverrideCell
                      planId={planId}
                      period={focussedPeriod}
                      mixId={card.mixId}
                      options={manual_heat_overrides ?? []}
                      disabled={false}
                      required
                    />
                  </Stack>
                )}
              </Box>
            </CardContent>
          </Card>
        ))}
        {mixCards.length === 0 && !isLoading && (
          <Typography color="text.secondary" textAlign="center">
            {t("noMixesFound")}
          </Typography>
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            onClick={handleDiscardChanges}
            disabled={!hasEdits || isSaving}
            fullWidth
          >
            {t("discardChanges")}
          </Button>
          <LoadingButton
            variant="contained"
            onClick={() => void handleSaveChanges()}
            disabled={!hasEdits}
            loading={isSaving}
            fullWidth
          >
            {t("saveChanges")}
          </LoadingButton>
        </Stack>
      </Stack>

      <MixEditDrawer
        open={selectedMix !== null}
        onClose={() => {
          setSelectedMix(null);
        }}
        mix={selectedMix}
        materials={materials}
        periodResults={periodResults}
        summarisedMixes={summarisedMixes ?? []}
        editedMixes={editedMixes}
        onMassChange={handleMassChange}
        massScaleFactor={massScaleFactor}
      />
    </>
  );
};

interface MixEditDrawerProps {
  open: boolean;
  onClose: () => void;
  mix: MixCardData | null;
  materials: MaterialRead[];
  periodResults: ProductionMixResultRead[];
  summarisedMixes: SummarisedProductionMixResultRead[];
  editedMixes: EditedMixesState;
  onMassChange: (
    mixId: number,
    basketNumber: string,
    materialName: string,
    mass: number
  ) => void;
  massScaleFactor: number;
}

const MixEditDrawer = ({
  open,
  onClose,
  mix,
  materials,
  periodResults,
  summarisedMixes,
  editedMixes,
  onMassChange,
  massScaleFactor,
}: MixEditDrawerProps) => {
  const { t } = useTenantTranslation();
  const { default_mix_breakdown } = useTenantData();
  const units = useUnitsFormatter(true);
  const massUnit =
    default_mix_breakdown === "small_mass" ? "small_mass" : "mass";

  const mixData = React.useMemo(() => {
    if (!mix) return null;

    const thisResult = periodResults.find((r) => r.mix.id === mix.mixId);
    if (!thisResult) return null;

    const summarisedMix = summarisedMixes.find((sm) => sm.mix.id === mix.mixId);
    const copperChemistry = summarisedMix?.mix.summary.chemistry.find(
      (c) => c.constraint_name === "Cu"
    );

    const basketMaterials = materials
      .filter((m) => m.addition_location === "basket")
      .sort((a, b) => a.deployment_ordering - b.deployment_ordering);

    const sortedBaskets = [...thisResult.mix.baskets].sort(
      (a, b) => a.basket_number - b.basket_number
    );

    const editedBaskets = editedMixes[mix.mixId];

    const baskets = sortedBaskets.map((basket) => ({
      basketNumber: basket.basket_number,
      materials: basketMaterials.map((material) => {
        const editedMass =
          editedBaskets?.[String(basket.basket_number)]?.[material.name];
        const originalMass = getMaterialMass(basket.layers, material.name);
        return {
          materialId: material.id,
          materialName: material.name,
          mass: editedMass ?? originalMass,
        };
      }),
    }));

    const numberOfHeats = thisResult.production.reduce(
      (sum, item) => sum + item.num_heats,
      0
    );

    return {
      mixId: thisResult.mix.id,
      groupName: mix.groupName,
      numHeats: numberOfHeats,
      originalCostPerTonne: mix.originalCostPerTonne,
      currentCostPerTonne:
        summarisedMix?.mix.summary.cost.display_cost_per_tonne ?? null,
      copperPercent: copperChemistry?.weight_percent_loc ?? null,
      baskets,
      basketMaterials,
    };
  }, [periodResults, mix, materials, summarisedMixes, editedMixes]);

  if (!mix) return null;

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 1400 }}
      slotProps={{
        paper: {
          sx: {
            height: "85vh",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          },
        },
      }}
    >
      <Stack sx={{ height: "100%", overflow: "hidden" }}>
        <Stack
          direction="row"
          alignItems="center"
          sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <IconButton onClick={onClose} edge="start">
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="h6" sx={{ ml: 1 }}>
            {mixData?.groupName ?? mix.groupName}
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            sx={{ ml: "auto" }}
            alignItems="center"
          >
            <Typography variant="body1">
              {mixData?.numHeats ?? mix.numHeats} {t("heats")}
            </Typography>
            <CostDisplay
              originalCost={
                mixData?.originalCostPerTonne ?? mix.originalCostPerTonne
              }
              currentCost={mixData?.currentCostPerTonne ?? null}
            />
            <CopperDisplay copper={mixData?.copperPercent ?? null} />
          </Stack>
        </Stack>

        {!mixData ? (
          <Stack alignItems="center" justifyContent="center" flex={1}>
            <CircularProgress />
          </Stack>
        ) : (
          <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `140px repeat(${mixData.baskets.length}, 1fr)`,
                gap: 0.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  p: 1,
                  bgcolor: "grey.100",
                  position: "sticky",
                  left: 0,
                }}
              >
                {t("scrap")}
              </Typography>
              {mixData.baskets.map((basket) => (
                <Typography
                  key={basket.basketNumber}
                  variant="body2"
                  sx={{
                    fontWeight: "bold",
                    p: 1,
                    bgcolor: "grey.100",
                    textAlign: "center",
                  }}
                >
                  {t("basket")} {basket.basketNumber} {units(massUnit)}
                </Typography>
              ))}

              {mixData.basketMaterials.map((material) => (
                <React.Fragment key={material.id}>
                  <Typography
                    variant="body2"
                    sx={{
                      p: 1,
                      bgcolor: "grey.50",
                      position: "sticky",
                      left: 0,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {material.name}
                  </Typography>
                  {mixData.baskets.map((basket) => {
                    const bm = basket.materials.find(
                      (m) => m.materialId === material.id
                    );
                    const displayValue = bm?.mass
                      ? Math.round(bm.mass * massScaleFactor * 10) / 10
                      : "";
                    return (
                      <Box
                        key={`${basket.basketNumber}-${material.id}`}
                        sx={{ p: 0.5 }}
                      >
                        <TextField
                          size="small"
                          type="number"
                          value={displayValue}
                          placeholder="0"
                          onChange={(e) => {
                            const displayedValue =
                              parseFloat(e.target.value) || 0;
                            const storedValue =
                              displayedValue / massScaleFactor;
                            onMassChange(
                              mixData.mixId,
                              String(basket.basketNumber),
                              material.name,
                              storedValue
                            );
                          }}
                          slotProps={{
                            htmlInput: {
                              sx: {
                                textAlign: "center",
                                padding: "6px",
                              },
                            },
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor:
                                  bm && bm.mass > 0
                                    ? "primary.main"
                                    : "grey.300",
                              },
                            },
                          }}
                          fullWidth
                        />
                      </Box>
                    );
                  })}
                </React.Fragment>
              ))}
            </Box>
          </Box>
        )}
      </Stack>
    </Drawer>
  );
};
